class SummerCampaignSlider {
    constructor(forms, SwiperCtor) {
        this.forms = forms;
        this.SwiperCtor = SwiperCtor;
        this.instances = [];
        this.activeModalInstance = null;
        this.modalId = 'summer-campaign-dare';
        this.modalRevealClass = 'summer-campaign-dare-revealed';
        this.bindModalClose();
        this.init();
    }

    bindModalClose() {
        window.addEventListener(`${this.modalId}-modal-closed`, () => {
            if (this.activeModalInstance?.reset) {
                this.activeModalInstance.reset();
            }

            this.activeModalInstance = null;
            this.clearModalCardContent();
        });
    }

    getModalElement() {
        const modal = document.querySelector(`[data-modal="${this.modalId}"]`);

        if (!modal) {
            console.warn(`[SummerCampaignSlider] Missing modal container for id "${this.modalId}".`);
        }

        return modal;
    }

    clearModalCardContent() {
        const modal = this.getModalElement();
        if (!modal) {
            return;
        }

        const modalCardTarget = modal.querySelector('[data-summer-modal-card]');
        if (modalCardTarget) {
            modalCardTarget.innerHTML = '';
        }

        modal.classList.remove(this.modalRevealClass);
    }

    applyCardBackgroundFallback(form) {
        const formDefaultBackground = form.dataset.defaultCardBackground || '';

        form.querySelectorAll('.summer-campaign-card-slide').forEach((slide) => {
            const cardBackground = slide.dataset.cardBackground || '';
            const resolvedBackground = cardBackground || formDefaultBackground;

            if (resolvedBackground) {
                slide.style.setProperty('--card-bg-image', `url("${resolvedBackground}")`);
            } else {
                slide.style.removeProperty('--card-bg-image');
            }
        });
    }

    getSourceSlide(form) {
        const activeSlide = form.querySelector('.summer-campaign-slider .swiper-slide-active');
        if (!activeSlide) {
            return null;
        }

        const slideIndex = activeSlide.getAttribute('data-swiper-slide-index');
        if (slideIndex === null) {
            return activeSlide;
        }

        const sourceSlide = form.querySelector(
            `.summer-campaign-slider .swiper-slide:not(.swiper-slide-duplicate)[data-swiper-slide-index="${slideIndex}"]`
        );

        return sourceSlide || activeSlide;
    }

    openModalWithSlide(slide) {
        const modal = this.getModalElement();
        const modalCardTarget = modal?.querySelector('[data-summer-modal-card]');

        if (!modal || !modalCardTarget) {
            console.warn(
                `[SummerCampaignSlider] Modal open aborted for "${this.modalId}" (container or [data-summer-modal-card] missing).`
            );
            return false;
        }

        const cardInner = slide.querySelector('.summer-campaign-card-inner');
        if (!cardInner) {
            return false;
        }

        const cardWrapper = document.createElement('div');
        cardWrapper.className = 'summer-campaign-modal-card';
        cardWrapper.appendChild(cardInner.cloneNode(true));
        const resolvedBackground = slide.style.getPropertyValue('--card-bg-image');
        if (resolvedBackground) {
            cardWrapper.style.setProperty('--card-bg-image', resolvedBackground);
        }

        modalCardTarget.innerHTML = '';
        modalCardTarget.appendChild(cardWrapper);
        modal.classList.remove(this.modalRevealClass);

        window.modalController.presentModal(this.modalId);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                modal.classList.add(this.modalRevealClass);
            });
        });

        return true;
    }

    init() {
        this.forms.forEach((form) => {
            if (form.dataset.summerCampaignBound === 'true') {
                return;
            }

            const sliderEl = form.querySelector('.summer-campaign-slider');
            const primaryButton = form.querySelector('[data-summer-action="primary"]');
            const statusText = form.querySelector('[data-summer-status]');

            if (!sliderEl || !primaryButton) {
                return;
            }

            this.applyCardBackgroundFallback(form);

            const swiper = new this.SwiperCtor(sliderEl, {
                speed: 250,
                spaceBetween: 20,
                initialSlide: 0,
                autoHeight: false,
                direction: 'horizontal',
                loop: true,
                autoplay: {
                    delay: 25,
                    disableOnInteraction: false,
                },
                slidesPerView: 1.65,
                centeredSlides: true,
                grabCursor: true,
                breakpoints: {
                    320: {
                        spaceBetween: 15,
                    },
                    640: {
                        slidesPerView: 1.65,
                        spaceBetween: 20,
                    },
                    991: {
                        slidesPerView: 2.65,
                        spaceBetween: 20,
                    },
                },
            });

            if (swiper.autoplay) {
                swiper.autoplay.stop();
            }

            const labels = {
                idle: primaryButton.dataset.labelIdle || 'Spin. Spin. Spin.',
                spinning: primaryButton.dataset.labelSpinning || 'Click to Stop Spinning',
            };

            const statuses = {
                idle: statusText?.dataset.statusIdle || 'Ready to spin.',
                spinning: statusText?.dataset.statusSpinning || 'Click again to stop the slider.',
            };

            const state = {
                phase: 'idle',
            };

            const setPhase = (phase) => {
                state.phase = phase;

                form.classList.toggle('is-spinning', phase === 'spinning');
                form.classList.toggle('is-modal-open', phase === 'modal');

                if (phase === 'spinning') {
                    primaryButton.textContent = labels.spinning;
                } else {
                    primaryButton.textContent = labels.idle;
                }

                if (statusText) {
                    if (phase === 'spinning') {
                        statusText.textContent = statuses.spinning;
                    } else {
                        statusText.textContent = statuses.idle;
                    }
                }
            };

            const reset = () => {
                if (swiper.autoplay) {
                    swiper.autoplay.stop();
                }

                swiper.slideToLoop(0, 250, false);
                swiper.updateProgress();
                setPhase('idle');
            };

            const startSpinning = (event) => {
                event.preventDefault();

                if (swiper.autoplay) {
                    swiper.autoplay.start();
                }

                setPhase('spinning');
            };

            const stopSpinning = (event) => {
                event.preventDefault();

                if (swiper.autoplay) {
                    swiper.autoplay.stop();
                }

                const selectedSlide = this.getSourceSlide(form);
                if (!selectedSlide) {
                    setPhase('idle');
                    return;
                }

                const opened = this.openModalWithSlide(selectedSlide);
                if (opened) {
                    this.activeModalInstance = { reset };
                    setPhase('modal');
                    return;
                }

                setPhase('idle');
            };

            primaryButton.addEventListener('click', (event) => {
                if (state.phase === 'idle') {
                    startSpinning(event);
                    return;
                }

                if (state.phase === 'spinning') {
                    stopSpinning(event);
                    return;
                }

                event.preventDefault();
            });

            primaryButton.dataset.labelIdle = labels.idle;
            primaryButton.dataset.labelSpinning = labels.spinning;

            if (statusText) {
                statusText.dataset.statusIdle = statuses.idle;
                statusText.dataset.statusSpinning = statuses.spinning;
            }

            setPhase('idle');
            form.dataset.summerCampaignBound = 'true';

            this.instances.push({
                form,
                swiper,
                reset,
            });
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const forms = Array.from(document.querySelectorAll('[data-summer-campaign]'));
    const SwiperCtor = window.swiper || window.Swiper;

    if (forms.length === 0 || !SwiperCtor || !window.modalController) {
        return;
    }

    window.SummerCampaignSlider = new SummerCampaignSlider(forms, SwiperCtor);
});
